import { useDeepEffect } from "hooks/useDeepEffect";
import { useCallback, useEffect, useState } from "react";
import { useServices } from "services";
import { useTxHttp } from "services/http";
import {
  PagedRealEstatesDto,
  RealEstateDto,
} from "services/profiles/dto/RealEstateDto";
import { RealEstateMatchingDto } from "services/profiles/dto/RealEstateMatchingDto";
import { initialState } from "../constants";
import {
  Params,
  RealEstateMatchingOption,
  RealEstatesMatching,
} from "../types";
import { containsPayload, flatten } from "../utils";

export const useAgentRealEstatesMatching = ({
  mode = "list",
}: RealEstateMatchingOption) => {
  const { get } = useTxHttp();

  const {
    endpoints: { mesBiensEnVente },
  } = useServices();

  const [state, setState] = useState<RealEstatesMatching>(initialState(mode));
  const memoizedState = useDeepEffect(state);

  const loadMore = useCallback(() => {
    setState((prev) => ({
      ...prev,
      params: { ...prev.params, page: +prev.params.page + 1 },
    }));
  }, []);

  const updateParams = (matching?: Partial<RealEstateMatchingDto>) => {
    setState((prev) => ({
      ...prev,
      params: { ...prev.params, ...matching },
    }));
  };

  const search = useCallback(
    async (params: Params) => {
      const response = await get<PagedRealEstatesDto>(
        mesBiensEnVente.rechercheGridOuListe,
        params
      );

      if (!response) return;

      const {
        data: { data: realEstates },
      } = response;

      setState((prev) => ({
        ...prev,
        pages: {
          ...prev.pages,
          [params.page]: realEstates.map((realEstate) => ({
            ...realEstate,
            page: params.page,
          })),
        },
        total: response.data.total,
        lastIds: realEstates.map((realEstate) => realEstate.id),
        currentPage: params.page,
      }));
    },
    [mesBiensEnVente.rechercheGridOuListe]
  );

  useEffect(() => {
    if (!memoizedState?.params) return;
    if (!containsPayload(memoizedState.params)) return;

    search(memoizedState.params);
  }, [memoizedState?.params]);

  return {
    loadMore,
    updateParams,
    realEstates: flatten(state.pages) as RealEstateDto[],
    total: state.total,
    lastIds: state.lastIds,
    page: state.currentPage,
  };
};
